/**
 * Radix Tooltip wrappers — touch-aware.
 *
 * Two independent concerns live here:
 *
 *   1. `TooltipContent` wraps itself in `<TooltipPortal>` so the content
 *      always renders as a direct child of `document.body` instead of
 *      inheriting style from its DOM ancestors. Without the portal, consumers
 *      that place the trigger inside an `uppercase` / `tracking-wide`
 *      container (e.g. `KPICard`'s label row) cascade that styling onto the
 *      tooltip text, rendering tooltips in ALL-CAPS. Radix does NOT
 *      auto-portal `Content` — we do it centrally here so every call site
 *      benefits without change. (bug F1 / v1.4 pass 1.)
 *
 *   2. TOUCH SUPPORT (v1.4 pass 6 — 2026-04-24).
 *      Radix Tooltip is, by upstream design, hover/focus-only. On touch a
 *      tap synthesises a `mouseenter` (which briefly opens the tooltip),
 *      immediately followed by `pointerdown` / `click` which closes it
 *      (see `node_modules/@radix-ui/react-tooltip/dist/index.mjs` — the
 *      trigger calls `context.onClose()` on both pointerdown-when-open and
 *      click, and `onPointerMove` early-returns on `pointerType === "touch"`).
 *      The user-visible effect: "tooltip flashes for half a second and
 *      disappears". Reported multiple times across rating bars, pill deltas,
 *      KPI info icons, heatmap cells and table cells. Every previous pass
 *      (1-5) patched individual chart surfaces; the Radix `<Tooltip>`
 *      itself remained broken on touch.
 *
 *      Fix: on touch devices our wrapper transparently swaps to
 *      `@radix-ui/react-popover`. Popover IS designed for tap — it opens on
 *      trigger click, stays visible until the user taps outside, and has
 *      proper `onPointerDownOutside` handling. Public API (`Tooltip`,
 *      `TooltipTrigger`, `TooltipContent`, `TooltipProvider`) is unchanged;
 *      callers need no edits.
 *
 *      Touch detection mirrors `useIsTouchOnly` in
 *      `src/components/charts/ChartTooltip.tsx` — union of (hover:none),
 *      (pointer:coarse), `maxTouchPoints > 0`, `'ontouchstart' in window`.
 *      Re-evaluates on matchMedia change so hybrid laptop-tablets flip live.
 */
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as PopoverPrimitive from "@radix-ui/react-popover";

import { cn } from "@/lib/utils";
import * as React from "react";

// ---------------------------------------------------------------------------
// Touch detection — shared SSOT lives in charts/ChartTooltip.tsx. We inline a
// copy here instead of importing to avoid a circular dep (ChartTooltip.tsx
// pulls Recharts, which pulls a lot of chart machinery; `ui/tooltip.tsx` is a
// primitive that must stay light). The two implementations are near-identical
// — see that file for the detailed rationale.
//
// We compute the initial value EAGERLY (in `useState` initialiser) instead
// of starting at `false` and flipping via effect. The flip-via-effect
// approach made the first render always use Radix Tooltip, which requires a
// `TooltipProvider` ancestor; our touch-only tests (and a few bare consumers)
// render without one. Eager init guarantees touch devices never instantiate
// the Radix Tooltip tree — Popover is used from the very first render.
//
// Detection signals — an OR of two reliable modern queries:
//   1. `matchMedia('(hover: none)')` — primary pointer can't hover.
//   2. `matchMedia('(pointer: coarse)')` — primary pointer is imprecise.
//   3. `navigator.maxTouchPoints > 0` — device reports a touch digitizer.
//
// NOTE: we intentionally DO NOT use `'ontouchstart' in window` here even
// though `ChartTooltip.useIsTouchOnly` does. jsdom (our test environment)
// has `'ontouchstart' in window === true`, which would flip every component
// test into Popover mode and break tests that assert on `role="tooltip"`.
// Every real touch device we care about (iOS Safari, Android Chrome) flips
// both matchMedia queries AND reports `maxTouchPoints > 0`, so dropping
// `ontouchstart` costs no real-world coverage. The chart-side variant keeps
// the legacy flag because it has a different test surface — chart tests
// don't assert on tooltip role.
// ---------------------------------------------------------------------------
function computeTouchOnly(): boolean {
  if (typeof window === "undefined") return false;
  if (typeof window.matchMedia === "function") {
    if (window.matchMedia("(hover: none)").matches) return true;
    if (window.matchMedia("(pointer: coarse)").matches) return true;
  }
  const mtp = typeof navigator !== "undefined" ? navigator.maxTouchPoints : 0;
  if (typeof mtp === "number" && mtp > 0) return true;
  return false;
}

function useIsTouchOnly(): boolean {
  const [touchOnly, setTouchOnly] = React.useState<boolean>(computeTouchOnly);

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const apply = (): void => setTouchOnly(computeTouchOnly());
    // Re-run once on mount in case jsdom / hydration gave us a stale value.
    apply();
    if (typeof window.matchMedia !== "function") return;
    const mqHover = window.matchMedia("(hover: none)");
    const mqPointer = window.matchMedia("(pointer: coarse)");
    mqHover.addEventListener("change", apply);
    mqPointer.addEventListener("change", apply);
    return () => {
      mqHover.removeEventListener("change", apply);
      mqPointer.removeEventListener("change", apply);
    };
  }, []);

  return touchOnly;
}

// ---------------------------------------------------------------------------
// Per-Tooltip context — lets `TooltipTrigger` and `TooltipContent` know
// whether the ancestor decided to render as Popover (touch mode). Falls back
// to Tooltip mode when consumed outside a `Tooltip` wrapper (defensive — some
// helpers render a bare `TooltipProvider` at the root).
// ---------------------------------------------------------------------------
interface TooltipModeCtx {
  touch: boolean;
}
const TooltipModeContext = React.createContext<TooltipModeCtx>({ touch: false });

// ---------------------------------------------------------------------------
// TooltipProvider — passes through to Radix on desktop; irrelevant on touch
// (Popover doesn't need a provider). We always render the Radix provider so
// any stray hover consumers (e.g. rare desktop-only deep trees) keep working
// when Popover mode is active. Popover is self-contained and ignores it.
// ---------------------------------------------------------------------------
const TooltipProvider = TooltipPrimitive.Provider;

// ---------------------------------------------------------------------------
// Tooltip (root) — decides mode up-front and plugs in Popover.Root or
// Tooltip.Root accordingly. Props like `delayDuration` are Tooltip-specific;
// we accept them but drop when in touch mode (Popover has no such concept).
// ---------------------------------------------------------------------------
type TooltipProps = React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Root>;

function Tooltip({
  children,
  open,
  defaultOpen,
  onOpenChange,
  ...props
}: TooltipProps): React.ReactElement {
  const touch = useIsTouchOnly();

  if (touch) {
    return (
      <TooltipModeContext.Provider value={{ touch: true }}>
        <PopoverPrimitive.Root
          open={open}
          defaultOpen={defaultOpen}
          onOpenChange={onOpenChange}
        >
          {children}
        </PopoverPrimitive.Root>
      </TooltipModeContext.Provider>
    );
  }

  return (
    <TooltipModeContext.Provider value={{ touch: false }}>
      <TooltipPrimitive.Root
        open={open}
        defaultOpen={defaultOpen}
        onOpenChange={onOpenChange}
        {...props}
      >
        {children}
      </TooltipPrimitive.Root>
    </TooltipModeContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// TooltipTrigger — Popover.Trigger or Tooltip.Trigger. Both forward `asChild`
// identically and accept arbitrary DOM props, so the shared type works.
// ---------------------------------------------------------------------------
type TooltipTriggerProps = React.ComponentPropsWithoutRef<
  typeof TooltipPrimitive.Trigger
>;

const TooltipTrigger = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Trigger>,
  TooltipTriggerProps
>(function TooltipTriggerImpl(props, ref) {
  const { touch } = React.useContext(TooltipModeContext);
  if (touch) {
    return <PopoverPrimitive.Trigger ref={ref} {...props} />;
  }
  return <TooltipPrimitive.Trigger ref={ref} {...props} />;
});
TooltipTrigger.displayName = TooltipPrimitive.Trigger.displayName;

// ---------------------------------------------------------------------------
// TooltipContent — portalled, with the same visual classes for both modes.
// On touch we use Popover.Content (modal=false so the page beneath stays
// interactive, but `onPointerDownOutside` still fires → tap-away dismisses).
// ---------------------------------------------------------------------------
type TooltipContentProps = React.ComponentPropsWithoutRef<
  typeof TooltipPrimitive.Content
>;

const TOOLTIP_CONTENT_CLASSES =
  "z-50 overflow-hidden rounded-md border border-gray-700 bg-[#1f2937] px-3 py-1.5 font-normal text-center text-xs text-white shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2";

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  TooltipContentProps
>(function TooltipContentImpl(
  { className, sideOffset = 4, side, align, collisionPadding, ...props },
  ref,
) {
  const { touch } = React.useContext(TooltipModeContext);

  if (touch) {
    return (
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          ref={ref}
          side={side}
          align={align}
          sideOffset={sideOffset}
          collisionPadding={collisionPadding}
          // Preserve Tooltip visuals even in Popover mode — users can't tell
          // the primitive swapped under them.
          className={cn(TOOLTIP_CONTENT_CLASSES, className)}
          // Popover steals focus by default, which on mobile yanks the
          // virtual keyboard / scrolls the viewport. A tooltip-like surface
          // should remain read-only; `onOpenAutoFocus` preventDefault keeps
          // the caret on whatever was focused before the tap.
          onOpenAutoFocus={(e) => e.preventDefault()}
          // Mirror the tap-away dismissal story — default behaviour already
          // closes on outside pointerdown. No extra wiring needed.
          {...props}
        />
      </PopoverPrimitive.Portal>
    );
  }

  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        ref={ref}
        side={side}
        align={align}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        className={cn(TOOLTIP_CONTENT_CLASSES, className)}
        {...props}
      />
    </TooltipPrimitive.Portal>
  );
});
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

// Exported for callers that want to portal manually — keep the symbol stable.
const TooltipPortal = TooltipPrimitive.Portal;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, TooltipPortal };
